/**
 * Product Pairing Visualizer
 */

class ProductPairingVisualizer extends HTMLElement {
  constructor() {
    super();
    
    this.selectors = {
      previewImages: '[data-preview-image]',
      placeholders: '[data-placeholder]',
      dropdownTriggers: '[data-dropdown-trigger]',
      dropdownContents: '[data-dropdown-content]',
      productOptions: '.f-product-option',
      addButton: '[data-add-bundle]',
      totalPrice: '[data-total-price]',
      cartForm: '[data-cart-form]',
      cartItems: '[data-cart-items]'
    };
    
    this.selectedProducts = new Map();
    this.openDropdown = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.cacheElements();
    this.attachEventListeners();
    this.initializeDefaults();
  }

  cacheElements() {
    this.previewImages = this.querySelectorAll(this.selectors.previewImages);
    this.placeholders = this.querySelectorAll(this.selectors.placeholders);
    this.dropdownTriggers = this.querySelectorAll(this.selectors.dropdownTriggers);
    this.dropdownContents = this.querySelectorAll(this.selectors.dropdownContents);
    this.productOptions = this.querySelectorAll(this.selectors.productOptions);
    this.addButton = this.querySelector(this.selectors.addButton);
    this.totalPriceElement = this.querySelector(this.selectors.totalPrice);
    this.cartForm = this.querySelector(this.selectors.cartForm);
    this.cartItemsContainer = this.querySelector(this.selectors.cartItems);
  }

  attachEventListeners() {
    
    // Dropdown triggers
    this.dropdownTriggers.forEach((trigger, index) => {

      trigger.addEventListener('click', (e) => {
  
        this.toggleDropdown(e);
      });
    });

    // Product options
    this.productOptions.forEach((option, index) => {

      option.addEventListener('click', (e) => {
  
        this.handleProductSelection(e);
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.f-product-dropdown')) {
        this.closeAllDropdowns();
      }
    });

    // Add to cart button
    if (this.addButton) {
      this.addButton.addEventListener('click', this.handleAddToCart.bind(this));
    }
  }

  initializeDefaults() {
    this.updateTotalPrice();
    this.validateBundle();
  }

  toggleDropdown(event) {
    event.stopPropagation();
    event.preventDefault();
    
    const trigger = event.currentTarget;
    const uniqueKey = trigger.dataset.dropdownTrigger;
    
    const content = this.querySelector(`[data-dropdown-content="${uniqueKey}"]`);
    const dropdown = trigger.closest('.f-product-dropdown');
    const isOpen = content && content.style.display === 'block';
    
    this.closeAllDropdowns();
    
    if (!isOpen && content) {
      content.style.display = 'block';
      dropdown.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.openDropdown = dropdown;
    }
  }

  closeAllDropdowns() {
    this.dropdownContents.forEach(content => {
      content.style.display = 'none';
    });
    
    this.dropdownTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      const dropdown = trigger.closest('.f-product-dropdown');
      if (dropdown) {
        dropdown.classList.remove('is-open');
      }
    });
    
    this.openDropdown = null;
  }

  handleProductSelection(event) {
    event.stopPropagation();

    const option = event.currentTarget;
    const productType = option.dataset.productType;
    const blockId = option.dataset.blockId; 
    const uniqueKey = `${productType}-${blockId}`; 
    const wasSelected = option.classList.contains('is-selected');

    const allOptions = this.querySelectorAll(`[data-product-type="${productType}"][data-block-id="${blockId}"]`);
    allOptions.forEach(opt => opt.classList.remove('is-selected'));

    if (wasSelected) {
      this.selectedProducts.delete(uniqueKey);
      this.updateSelection(null, uniqueKey);
      this.updateDropdownTrigger(uniqueKey, null);
    } else {
      option.classList.add('is-selected');
      this.updateSelection(option, uniqueKey);
      this.updateDropdownTrigger(uniqueKey, option);
    }

    this.updateTotalPrice();
    this.validateBundle();
    this.closeAllDropdowns();
  }

  updateSelection(option, productType) {
  if (!option) {
    this.updatePreviewImage(productType, null);
    return;
  }

  const variantId = option.dataset.variantId;
  const price = parseFloat(option.dataset.price);
  const image = option.dataset.image;
  const title = option.dataset.title;

  this.selectedProducts.set(productType, {
    variantId,
    price,
    image,
    title,
    productType
  });

  this.updatePreviewImage(productType, image);
  }

  updateDropdownTrigger(uniqueKey, option) {
    const trigger = this.querySelector(`[data-dropdown-trigger="${uniqueKey}"]`);
    if (!trigger) return;

    const triggerText = trigger.querySelector('[data-trigger-text]');

    if (option) {
      const title = option.dataset.title;
      const price = this.formatMoney(parseFloat(option.dataset.price));
      if (triggerText) {
        triggerText.innerHTML = `
          <span class="f-trigger-selected">
            <span class="f-trigger-title">${title}</span>
            <span class="f-trigger-price">${price}</span>
          </span>
        `;
      }
      trigger.classList.add('has-selection');
    } else {
      if (triggerText) {
        triggerText.innerHTML = `<span class="f-trigger-placeholder">Choose product</span>`;
      }
      trigger.classList.remove('has-selection');
    }
  }


  updatePreviewImage(uniqueKey, imageUrl) {
    const previewLayer = this.querySelector(`[data-layer="${uniqueKey}"]`);
    const previewImage = this.querySelector(`[data-preview-image="${uniqueKey}"]`);
    const placeholder = this.querySelector(`[data-placeholder="${uniqueKey}"]`); 

    if (previewImage && previewLayer) {
      if (imageUrl) {
        previewImage.src = imageUrl;
        previewImage.style.display = 'block';
        const size = previewLayer.dataset.size || 30;
        previewLayer.style.setProperty('--layer-size', `${size}%`);
        
        if (placeholder) {
          placeholder.style.display = 'none';
        }
      } else {
        previewImage.style.display = 'none';
        
        if (placeholder) {
          placeholder.style.display = 'flex';
        }
      }
    }
  }


  updateTotalPrice() {
    let total = 0;
    this.selectedProducts.forEach(product => {
      total += product.price;
    });

    if (this.totalPriceElement) {
      if (this.selectedProducts.size > 0) {
        this.totalPriceElement.textContent = this.formatMoney(total);
      } else {
        this.totalPriceElement.textContent = '$0.00';
      }
    }
  }

  validateBundle() {
    // Get only required dropdowns
    const requiredTriggers = Array.from(this.dropdownTriggers).filter(
      trigger => trigger.dataset.required === 'true' || trigger.hasAttribute('data-required')
    );
    
    // If no required triggers, just check if at least one product is selected
    if (requiredTriggers.length === 0) {
      const hasAnySelection = this.selectedProducts.size > 0;
      if (this.addButton) {
        this.addButton.disabled = !hasAnySelection;
      }
      return;
    }
    
    // Check if all required product types are selected
    const requiredTypes = requiredTriggers.map(
      trigger => trigger.dataset.dropdownTrigger
    );
    
    let allRequiredSelected = requiredTypes.every(type => this.selectedProducts.has(type));

    // Enable/disable add to cart button
    if (this.addButton) {
      this.addButton.disabled = !allRequiredSelected;
    }
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    if (this.selectedProducts.size === 0) {
      return;
    }

    const originalText = this.addButton.textContent;
    this.addButton.disabled = true;
    this.addButton.textContent = 'Adding...';

    try {
      // Build items array for cart
      const items = Array.from(this.selectedProducts.values()).map(product => ({
        id: product.variantId,
        quantity: 1
      }));

      // Build FormData with proper format for multiple items
      const formData = new FormData();
      
      // Add each item individually with proper array format
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });

      // Get cart drawer sections if exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
      }

      // Get main cart sections if exists
      const mainCart = document.querySelector('cart-items');
      if (mainCart && typeof mainCart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          mainCart.getSectionsToRender().map((section) => section.section)
        );
      }

      if (mainCart || cartDrawer) {
        formData.append('sections_url', window.location.pathname);
      }

      // Add multiple items to cart using the same endpoint as product-form
      const response = await fetch(window.FoxThemeSettings.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
      }

      const result = await response.json();

      // Check for errors in response
      if (result.status) {
        throw new Error(result.description || result.message);
      }

      // Success - emit the event that CartDrawer is listening for
      window.FoxThemeEvents.emit('ON_ITEM_ADDED', result);

      // Show success message
      if (!window.FoxThemeSettings.cart.openDrawerWhenAdded || window.FoxThemeSettings.cart.cartType === 'page') {
        this.showSuccessMessage();
      }

      // Update cart count if cart type is page
      if (window.FoxThemeSettings.cart.cartType === 'page') {
        window.FoxThemeCartHelpers.getState().then(state => {
          window.FoxThemeCartHelpers.updateCartCount(state.item_count);
        });
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage(error.message);
    } finally {
      this.addButton.disabled = false;
      this.addButton.textContent = originalText;
    }
  }

  showSuccessMessage() {
    // Use FoxTheme notification system
    if (window.FoxTheme && window.FoxTheme.Notification) {
      window.FoxTheme.Notification.show({
        target: document.body,
        method: 'appendChild',
        type: 'success',
        message: window.FoxThemeStrings?.item_added || 'Set added to cart successfully!',
        last: 3000,
        sticky: true
      });
    } else {
      // Fallback notification
      this.showFallbackNotification('Set added to cart!', 'success');
    }
  }

  showErrorMessage(message) {
    if (window.FoxTheme && window.FoxTheme.Notification) {
      window.FoxTheme.Notification.show({
        target: document.body,
        method: 'appendChild',
        type: 'warning',
        message: message || 'Failed to add items to cart',
        last: 3000,
        sticky: true
      });
    } else {
      this.showFallbackNotification(message || 'Error adding to cart', 'error');
    }
  }

  showFallbackNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `f-notification f-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      border-radius: 8px;
      z-index: 9999;
      animation: slideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.FoxThemeSettings?.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Register custom element
if (!customElements.get('product-pairing-visualizer')) {
  customElements.define('product-pairing-visualizer', ProductPairingVisualizer);
}